import { DropdownMenuSectionTitle } from '@ardoq/dropdown-menu-ui';
import { FlexBox } from '@ardoq/layout';
import type * as React from 'react';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { GroupBase, GroupProps, OptionProps } from 'react-select';
import { Text } from '@ardoq/typography';
import { SectionSubtitle } from './view/atoms';
import { SourceOption } from './SourceOption';
import { Axis, AxisSourceGroupKind, AxisSourceOption } from './types';

export type AxisSourceGroup = GroupBase<AxisSourceOption> & {
  groupKind: AxisSourceGroupKind;
};

type SourceGroupTitleProps = {
  groupKind: AxisSourceGroupKind;
  axis: Axis;
  i18nLocale: I18nLocale;
};

const isRelatedComponentsGroupKind = (
  groupKind: AxisSourceGroupKind
): boolean => groupKind === AxisSourceGroupKind.RELATED_COMPONENTS;

const isParentRelationshipGroupKind = (
  groupKind: AxisSourceGroupKind
): boolean => groupKind === AxisSourceGroupKind.PARENT_RELATIONSHIP;

const isRowAxis = (axis: Axis): boolean => axis === Axis.ROW;

const SourceGroupTitle = ({
  groupKind,
  axis,
  i18nLocale,
}: SourceGroupTitleProps) => {
  if (isParentRelationshipGroupKind(groupKind)) {
    return (
      <FlexBox flexDirection="column" gap="xxsmall">
        <Text>
          {t(
            i18nLocale,
            'tabview:componentMatrix.leftMenu.parentRelationship',
            'Hierarchy'
          )}
        </Text>
        <SectionSubtitle>
          {isRowAxis(axis)
            ? t(
                i18nLocale,
                'tabview:componentMatrix.leftMenu.parentRelationshipRowSubtitle',
                'Choose a parent to set the row headers.'
              )
            : t(
                i18nLocale,
                'tabview:componentMatrix.leftMenu.parentRelationshipColumnSubtitle',
                'Choose a parent to set the column headers.'
              )}
        </SectionSubtitle>
      </FlexBox>
    );
  }

  if (isRelatedComponentsGroupKind(groupKind)) {
    return (
      <FlexBox flexDirection="column" gap="xxsmall">
        <Text>
          {t(
            i18nLocale,
            'tabview:componentMatrix.leftMenu.relatedComponents',
            'Related components'
          )}
        </Text>
        <SectionSubtitle>
          {isRowAxis(axis)
            ? t(
                i18nLocale,
                'tabview:componentMatrix.leftMenu.relatedComponentsRowSubtitle',
                'Choose a reference to set the row headers.'
              )
            : t(
                i18nLocale,
                'tabview:componentMatrix.leftMenu.relatedComponentsColumnSubtitle',
                'Choose a reference to set the column headers.'
              )}
        </SectionSubtitle>
      </FlexBox>
    );
  }

  return (
    <FlexBox flexDirection="column" gap="xxsmall">
      <Text>
        {t(
          i18nLocale,
          'tabview:componentMatrix.leftMenu.listMultiListFields',
          'List and multi-list fields'
        )}
      </Text>
      <SectionSubtitle>
        {isRowAxis(axis)
          ? t(
              i18nLocale,
              'tabview:componentMatrix.leftMenu.listMultiListFieldsRowSubtitle',
              'Choose a field to set the row headers.'
            )
          : t(
              i18nLocale,
              'tabview:componentMatrix.leftMenu.listMultiListFieldsColumnSubtitle',
              'Choose a field to set the column headers.'
            )}
      </SectionSubtitle>
    </FlexBox>
  );
};

const isAxisSourceGroup = (
  group: GroupBase<AxisSourceOption>
): group is AxisSourceGroup => 'groupKind' in group;

type SourceGroupProps = GroupProps<
  AxisSourceOption,
  false,
  GroupBase<AxisSourceOption>
> & {
  axis: Axis;
  i18nLocale: I18nLocale;
};

const SourceGroup = ({
  data,
  axis,
  i18nLocale,
  children,
}: SourceGroupProps) => {
  if (!isAxisSourceGroup(data)) {
    return <>{children}</>;
  }

  return (
    <>
      <DropdownMenuSectionTitle>
        <SourceGroupTitle
          groupKind={data.groupKind}
          axis={axis}
          i18nLocale={i18nLocale}
        />
      </DropdownMenuSectionTitle>
      {children}
    </>
  );
};

type SourceSelectComponents = {
  Option: (
    props: OptionProps<AxisSourceOption, false, GroupBase<AxisSourceOption>>
  ) => React.JSX.Element;
  Group: (
    props: GroupProps<AxisSourceOption, false, GroupBase<AxisSourceOption>>
  ) => React.JSX.Element;
};

const buildSourceSelectComponents = ({
  axis,
  i18nLocale,
  onDeselect,
}: {
  axis: Axis;
  i18nLocale: I18nLocale;
  onDeselect: VoidFunction;
}): SourceSelectComponents => {
  const Option = (
    props: OptionProps<AxisSourceOption, false, GroupBase<AxisSourceOption>>
  ) => <SourceOption {...props} onDeselect={onDeselect} />;

  const Group = (
    props: GroupProps<AxisSourceOption, false, GroupBase<AxisSourceOption>>
  ) => <SourceGroup {...props} axis={axis} i18nLocale={i18nLocale} />;

  return { Option, Group };
};

export { buildSourceSelectComponents, type SourceSelectComponents };
